import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { updateEncounter } from '../../../redux/slices/encountersSlice';
import { setCareTeam } from '../../../services/careCoordinationService';
import EncounterBaseRefactored, { syncEncounterToMedicalHistory } from '../EncounterBaseRefactored';
import WorkflowTaskList from '../WorkflowTaskList';
import { useEncounterFormState } from '../hooks/useEncounterFormState';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
// Note: Data fetching is handled by EncounterDetails.js (parent component)

dayjs.extend(utc);
dayjs.extend(timezone);

const SurgicalEncounterDetails = ({ onRefreshEncounter }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [careTeamChanges, setCareTeamChanges] = useState(null);
  const [resetPendingChanges, setResetPendingChanges] = useState(() => () => {});

  // Data is fetched by parent component (EncounterDetails.js) and stored in Redux
  const encounter = useSelector(state => state.encounters.selectedEncounter);
  const loading = useSelector(state => state.encounters.loading);
  const error = useSelector(state => state.encounters.error);
  const selectedPatient = useSelector(state => state.patients.selectedPatient);
  const prescriptions = useSelector(state => state.prescriptions.prescriptions);
  const recentEncounters = useSelector(state => state.encounters.recentEncounters);

  // Use the refactored hook for form state management
  const {
    formData,
    setFormData,
    hasChanges,
    resetChanges
  } = useEncounterFormState(encounter, 'surgical');

  const getDisplayDate = () => {
    if (!encounter?.createdAt) return 'Unknown Date';
    let date = new Date(encounter.createdAt);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const userTz = dayjs.tz.guess();
    return dayjs(date).tz(userTz).format('YYYY-MM-DD HH:mm');
  };

  const getAdmissionInfo = () => {
    const admissionDate = getDisplayDate();
    const roomInfo = encounter?.surgicalDetails?.operatingRoom ? `OR ${encounter.surgicalDetails.operatingRoom}` : 'No OR Assigned';
    return `Admission: ${admissionDate} • ${roomInfo}`;
  };

  const handleSave = async () => {
    if (!formData) return;
    
    setSaving(true);
    try {
      const previousStatus = encounter?.status;
      const newStatus = formData.status;
      
      const updateData = {
        ...formData,
        createdAt: new Date().toISOString(),
        vitals: formData.vitals,
        notes: formData.notes,
        symptoms: formData.symptoms,
        actionPlan: formData.actionPlan,
        provider: formData.provider,
        department: formData.department,
        unit: formData.unit,
        chiefComplaint: formData.chiefComplaint,
        initialAssessment: formData.initialAssessment,
        surgicalDetails: formData.surgicalDetails
      };
      
      // Save encounter data
      await dispatch(updateEncounter({ encounterId: encounter.id, updateData })).unwrap();
      
      // Save care team changes if any
      if (careTeamChanges && encounter?.patientId) {
        await setCareTeam(encounter.patientId, careTeamChanges, 'system');
        setCareTeamChanges(null);
      }
      
      // Sync to medical history when encounter is completed/discharged
      // This will sync diagnoses to Problem List and surgical details to Surgical History
      await syncEncounterToMedicalHistory({
        patientId: encounter?.patientId,
        facilityId: encounter?.facilityId || selectedPatient?.facilityId,
        encounterData: {
          ...updateData,
          type: 'surgical',
          encounterType: 'surgical'
        },
        encounterId: encounter.id,
        newStatus,
        previousStatus
      });
      
      // Reset pending changes state
      resetPendingChanges();
      resetChanges();
    } catch (error) {
      console.error('Error saving surgical encounter:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/patients/${encounter?.patientId}/encounters`);
  };

  const renderTaskList = () => (
    <WorkflowTaskList encounterId={encounter?.id} />
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading surgical encounter details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error loading surgical encounter: {error}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back to Encounters
        </Button>
      </Box>
    );
  }

  if (!encounter) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Surgical encounter not found
        </Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back to Encounters
        </Button>
      </Box>
    );
  }

  return (
    <EncounterBaseRefactored
      mode="edit"
      encounter={encounter}
      patient={selectedPatient}
      prescriptions={prescriptions}
      recentEncounters={recentEncounters}
      formData={formData}
      setFormData={setFormData}
      onSave={handleSave}
      onBack={handleBack}
      loading={loading}
      saving={saving}
      title="Surgical Procedure"
      subtitle={getAdmissionInfo()}
      renderTaskList={renderTaskList}
      showExtraTabs={true}
      facilityId={encounter?.facilityId || selectedPatient?.facilityId || ''}
      encounterType="surgical"
      onCareTeamChange={setCareTeamChanges}
      onSaveSuccess={setResetPendingChanges}
      customHeaderIcon={<LocalHospitalIcon sx={{ fontSize: 32, color: 'warning.main' }} />}
      onRefreshEncounter={onRefreshEncounter}
      hasChanges={hasChanges}
      showPatientInfoInHeader={false}
    />
  );
};

export default SurgicalEncounterDetails; 